/** Matches DEFAULT_STARVATION_MINUTES in capacity_pool_service.py. */
export const DEFAULT_POOL_STARVATION_MINUTES = 30;

/** One row of the editor. Kept as strings so a half-typed number is not lost. */
export interface CapacityPoolDraft {
  // Stable React key, so removing a row does not shift focus onto its neighbour.
  id: string;
  accelerator: string;
  maxGpus: string;
}

export interface CapacityPool {
  accelerator: string;
  max_gpus: number;
}

let nextDraftId = 0;

export function newPoolDraft(
  accelerator: string = '',
  maxGpus: string = '',
): CapacityPoolDraft {
  nextDraftId += 1;
  return { id: `pool-${nextDraftId}`, accelerator, maxGpus };
}

/**
 * Turns the editor rows into the `capacity_pools` config value, or says what is
 * wrong. Rows left completely blank are dropped. The server checks all of this
 * again; this only saves a round trip.
 */
export function parseCapacityPools(
  drafts: CapacityPoolDraft[],
): { pools: CapacityPool[] } | { error: string } {
  const pools: CapacityPool[] = [];
  const seen = new Set<string>();
  for (const draft of drafts) {
    const accelerator = draft.accelerator.trim();
    const maxGpus = draft.maxGpus.trim();
    if (!accelerator && !maxGpus) {
      // A blank row the user added and never filled in.
      // eslint-disable-next-line no-continue
      continue;
    }
    if (!accelerator) {
      return { error: 'Every capacity pool needs an accelerator, e.g. H100.' };
    }
    const count = Number(maxGpus);
    if (!Number.isInteger(count) || count < 1) {
      return {
        error: `The ${accelerator} capacity pool needs a whole number of GPUs, at least 1.`,
      };
    }
    const key = accelerator.toLowerCase();
    if (seen.has(key)) {
      return {
        error: `${accelerator} is listed twice in the capacity pools. Use one pool per card.`,
      };
    }
    seen.add(key);
    pools.push({ accelerator, max_gpus: count });
  }
  return { pools };
}
